import {
  useReactFlow,
  applyNodeChanges,
  type Node,
  type OnMoveEnd,
  type OnNodesChange,
} from '@xyflow/react';
import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import {useAtomValue, useSetAtom} from 'jotai';
import {isDraggingNodeAtom} from '@features/frame/partialEdit/atoms';
import {useSetSelectedScreenIds} from '@features/activeScreenIdContext/useSetSelectedScreenIds';
import {useSelectedScreenIds} from '@features/activeScreenIdContext/useSelectedScreenIds';
import {FlowContext} from '@features/contexts/FlowContext';
import {trpc} from '@features/utils/trpc';
import {useViewport} from './useViewport';
import {
  computeCanvasLayout,
  type LayoutNodeCache,
} from '../utils/buildCanvasNodes';
import amplitudeService from '@features/analytics/amplitudeService';
import {getStoredViewport, saveViewport} from '../localStorage/viewport';
import {getStoredLastSelectedScreenId} from '../localStorage/lastSelectedScreen';
import {streamingGenTypeByScreenAtom} from '@features/ai/streaming/streamingGenTypeAtom';
import {useFlowsScreens} from '../hooks/useFlowScreens';
import {useComponentReflow} from '../hooks/useComponentReflow';

export type {FrameNodeType} from '../utils/buildCanvasNodes';

// Components flows never consume streamingGenTypes (component structures are
// always 'web-html-css' server-side and the platform never switches mid-gen),
// so we pass this stable empty reference to short-circuit the layout useMemo.
const EMPTY_STREAMING_GEN_TYPES: Readonly<Partial<Record<string, never>>> =
  Object.freeze({});

const EMPTY_SCREENS: never[] = [];

export const useCanvas = () => {
  const {flow} = useContext(FlowContext);
  const {id: flowId, genType: flowGenType} = flow;

  const trpcUtils = trpc.useUtils();
  const setSelectedScreenIds = useSetSelectedScreenIds();
  const {data: screens = EMPTY_SCREENS} = useFlowsScreens(flowId);
  const streamingGenTypesRaw = useAtomValue(streamingGenTypeByScreenAtom);
  const streamingGenTypes =
    flowGenType === 'components'
      ? EMPTY_STREAMING_GEN_TYPES
      : streamingGenTypesRaw;
  const updateScreenPositions = trpc.screens.updateScreenPositions.useMutation({
    onSuccess: () => void trpcUtils.flow.getUndoStatus.invalidate({flowId}),
  });
  const {fitViewToChat, fitViewToAll} = useViewport();
  const {setViewport} = useReactFlow();
  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
  const selectedScreenIds = useSelectedScreenIds();
  const prevSelectedSizeRef = useRef(selectedScreenIds.length);

  const {handleDimensionChanges, cancelPendingReflow, componentDims} =
    useComponentReflow(flowId, flowGenType);
  const isDraggingRef = useRef(false);
  const setIsDraggingNode = useSetAtom(isDraggingNodeAtom);
  const [nodeCache] = useState<LayoutNodeCache>(() => new Map());

  // Clear all selection on flow switch. The provider's URL-sync effect will
  // repopulate the primary active id from the URL immediately after.
  useEffect(() => {
    setSelectedScreenIds([]);
    prevSelectedSizeRef.current = 0;
  }, [flowId, setSelectedScreenIds]);

  useEffect(() => {
    const prevSize = prevSelectedSizeRef.current;
    const currentSize = selectedScreenIds.length;

    if (prevSize <= 1 && currentSize > 1) {
      amplitudeService.logEvent('[Canvas] multi-select started', {
        selectedCount: currentSize,
      });
    }

    prevSelectedSizeRef.current = currentSize;
  }, [selectedScreenIds.length]);

  // Base nodes derived from server data — stable during drag.
  // The nodeCache ref preserves per-node referential identity across
  // recomputations whose scalar inputs are unchanged, so xyflow's per-node
  // store subscriptions only fire for nodes that actually changed.
  const baseNodes = useMemo(
    () =>
      computeCanvasLayout({
        screens,
        streamingGenTypes,
        flowGenType,
        flowId,
        selectedScreenIds,
        componentDims,
        nodeCache,
      }),
    [
      screens,
      streamingGenTypes,
      flowGenType,
      flowId,
      selectedScreenIds,
      componentDims,
      nodeCache,
    ],
  );

  // Local nodes state for ReactFlow visual feedback during drag
  const [nodes, setNodes] = useState<Node[]>(baseNodes);
  const [prevBaseNodes, setPrevBaseNodes] = useState(baseNodes);

  // Sync from base nodes when server data changes (not during drag).
  // Uses "setState during render" pattern to avoid cascading useEffect renders.
  if (prevBaseNodes !== baseNodes && draggingNodeId === null) {
    setPrevBaseNodes(baseNodes);
    setNodes(baseNodes);
  }

  const onNodesChange: OnNodesChange = useCallback(
    (changes) => {
      const hasSelectionUpdates = changes.some(
        (change) => change.type === 'select' || change.type === 'remove',
      );

      if (hasSelectionUpdates) {
        setSelectedScreenIds((prev) => {
          // Use a Set for O(1) presence checks while preserving order: append
          // newly-selected ids at the end, keep existing ordering otherwise.
          const present = new Set(prev);
          let next = prev;
          let mutated = false;

          for (const change of changes) {
            if (change.type === 'select') {
              if (change.selected) {
                if (!present.has(change.id)) {
                  if (!mutated) {
                    next = [...prev];
                    mutated = true;
                  }
                  next.push(change.id);
                  present.add(change.id);
                }
              } else if (present.has(change.id)) {
                if (!mutated) {
                  next = [...prev];
                  mutated = true;
                }
                next = next.filter((id) => id !== change.id);
                // eslint-disable-next-line drizzle/enforce-delete-with-where -- This is a Set, not a drizzle query
                present.delete(change.id);
              }
            }
            if (change.type === 'remove' && present.has(change.id)) {
              if (!mutated) {
                next = [...prev];
                mutated = true;
              }
              next = next.filter((id) => id !== change.id);
              // eslint-disable-next-line drizzle/enforce-delete-with-where -- This is a Set, not a drizzle query
              present.delete(change.id);
            }
          }

          return mutated ? next : prev;
        });
      }

      // Apply position changes to local state for visual drag feedback
      setNodes((prev) => applyNodeChanges(changes, prev));

      // Sync positions to tRPC cache only when not dragging
      if (!isDraggingRef.current) {
        const positionChanges = changes.filter(
          (change): change is Extract<typeof change, {type: 'position'}> =>
            change.type === 'position' && change.position !== undefined,
        );

        if (positionChanges.length > 0) {
          trpcUtils.screens.getFlowScreens.setData({flowId}, (oldScreens) => {
            if (!oldScreens) return oldScreens;

            const positionUpdates = new Map<string, {x: number; y: number}>();
            for (const change of positionChanges) {
              if (change.position) {
                positionUpdates.set(change.id, change.position);
              }
            }

            return oldScreens.map((screen) => {
              const newPosition = positionUpdates.get(screen.id);
              if (newPosition) {
                return {...screen, x: newPosition.x, y: newPosition.y};
              }
              return screen;
            });
          });
        }
      }

      handleDimensionChanges(changes);
    },
    [
      flowId,
      trpcUtils.screens.getFlowScreens,
      setSelectedScreenIds,
      handleDimensionChanges,
    ],
  );

  const initialized = useRef(false);
  useEffect(() => {
    if (screens.length === 0) return;
    if (initialized.current) return;

    const storedViewport = getStoredViewport(flowId);

    if (storedViewport) {
      setTimeout(() => void setViewport(storedViewport), 0);
    }

    const savedScreenId = getStoredLastSelectedScreenId(flowId);
    const lastScreen = screens[screens.length - 1];
    const screenToSelect =
      screens.find((s) => s.id === savedScreenId) ?? lastScreen;

    if (!storedViewport) {
      if (screens.length > 1) {
        setTimeout(() => void fitViewToAll({duration: 0}), 0);
      } else {
        setTimeout(
          () => void fitViewToChat(screenToSelect.id, {duration: 0}),
          0,
        );
      }
    }
    setSelectedScreenIds([screenToSelect.id]);

    initialized.current = true;
  }, [
    screens,
    fitViewToChat,
    fitViewToAll,
    setSelectedScreenIds,
    flowId,
    setViewport,
  ]);

  const onMoveEnd: OnMoveEnd = useCallback(
    (_event, viewport) => {
      saveViewport(flowId, viewport);
    },
    [flowId],
  );

  const onNodeDragStart = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (node.type !== 'frame' && node.type !== 'component') return;
      isDraggingRef.current = true;
      setDraggingNodeId(node.id);
      setIsDraggingNode(true);
    },
    [setIsDraggingNode],
  );

  const onNodeDragStop = useCallback(
    (_event: React.MouseEvent, _node: Node, nodes: Node[]) => {
      isDraggingRef.current = false;
      setIsDraggingNode(false);
      cancelPendingReflow();
      void trpcUtils.screens.getFlowScreens.cancel({flowId});

      const frameNodes = nodes.filter(
        (n) => n.type === 'frame' || n.type === 'component',
      );

      if (frameNodes.length === 0) {
        setDraggingNodeId(null);
        return;
      }

      // Update cache with final positions before clearing drag state
      trpcUtils.screens.getFlowScreens.setData({flowId}, (oldScreens) => {
        if (!oldScreens) return oldScreens;

        const positionUpdates = new Map<string, {x: number; y: number}>();
        for (const frameNode of frameNodes) {
          positionUpdates.set(frameNode.id, {
            x: Math.round(frameNode.position.x),
            y: Math.round(frameNode.position.y),
          });
        }

        return oldScreens.map((screen) => {
          const newPosition = positionUpdates.get(screen.id);
          if (newPosition) {
            return {...screen, x: newPosition.x, y: newPosition.y};
          }
          return screen;
        });
      });

      setDraggingNodeId(null);

      amplitudeService.logEvent('[Flow] canvas nodes dragged', {
        count: frameNodes.length,
        nodes: frameNodes.map((n) => ({
          screenId: n.id,
          x: Math.round(n.position.x),
          y: Math.round(n.position.y),
        })),
      });

      updateScreenPositions.mutate({
        flowId,
        updates: frameNodes.map((frameNode) => ({
          screenId: frameNode.id,
          x: Math.round(frameNode.position.x),
          y: Math.round(frameNode.position.y),
        })),
      });
    },
    [
      cancelPendingReflow,
      flowId,
      setIsDraggingNode,
      trpcUtils.screens.getFlowScreens,
      updateScreenPositions,
    ],
  );

  return {
    nodes,
    onNodesChange,
    onNodeDragStart,
    onNodeDragStop,
    onMoveEnd,
    isDragging: draggingNodeId !== null,
  };
};
